/**
 * Header Navigation - Vanilla JS
 * Handles mobile menu toggle and scroll management
 */

import {
  ready,
  $,
  $$,
  toggleClass,
  hasClass,
  slideUp,
  slideDown,
  attr,
} from "./utils.js";

ready(() => {
  let hamburgerOpen = false;
  let scrollTop = 0;
  let scrollLeft = 0;

  const hamburger = $(".hamburger");
  const mobileNav = $(".mobile-navigation");
  const backdrop = $(".mobile-navigation__backdrop");
  const body = document.body;

  // Hamburger menu toggle
  if (hamburger && mobileNav) {
    hamburger.addEventListener("click", (event) => {
      event.preventDefault();
      toggleClass(mobileNav, "is-open");
      toggleClass(body, "menu-open");

      // Update aria-expanded attribute
      const isOpen = hasClass(mobileNav, "is-open");
      attr(hamburger, "aria-expanded", isOpen);

      if (!hamburgerOpen) {
        hamburgerOpen = true;
        disableScroll();
      } else {
        hamburgerOpen = false;
        enableScroll();
      }
    });
  }

  // Close menu on backdrop click
  if (backdrop && mobileNav) {
    backdrop.addEventListener("click", () => {
      mobileNav.classList.remove("is-open");
      body.classList.remove("menu-open");
      if (hamburger) attr(hamburger, "aria-expanded", false);
      hamburgerOpen = false;
      enableScroll();
    });
  }

  // Mobile navigation submenu setup
  if (mobileNav) {
    const navState = $(".mobile-navigation__state");
    const clickable = navState ? attr(navState, "data-clickable") : "false";

    // Add 'has-sub' class to parent items
    $$(".mobile-navigation li").forEach((li) => {
      if (li.querySelector("ul")) {
        li.classList.add("has-sub");
      }
    });

    // Add caret icons
    $$(".mobile-navigation .has-sub > a").forEach((link) => {
      const caret = document.createElement("em");
      caret.className = "mobile-navigation__caret";
      link.after(caret);
    });

    // Make links clickable or carets only
    if (clickable === "true") {
      $$(".mobile-navigation .has-sub > .mobile-navigation__caret").forEach(
        (caret) => {
          caret.classList.add("trigger-caret");
        },
      );
    } else {
      $$(".mobile-navigation .has-sub > a").forEach((link) => {
        link.classList.add("trigger-caret");
        attr(link, "href", "javascript:;");
      });
    }

    // Menu open/close on click
    $$(".mobile-navigation .has-sub > .trigger-caret").forEach((trigger) => {
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        const parentLi = this.parentElement;
        const submenu = parentLi.querySelector("ul");

        if (hasClass(parentLi, "is-open")) {
          // Close this menu
          parentLi.classList.remove("is-open");
          parentLi
            .querySelectorAll("li")
            .forEach((li) => li.classList.remove("is-open"));
          parentLi.querySelectorAll("ul").forEach((ul) => slideUp(ul, 200));
        } else {
          // Open this menu
          parentLi.classList.add("is-open");
          if (submenu) slideDown(submenu, 200);

          // Close siblings
          const siblings = Array.from(parentLi.parentElement.children).filter(
            (child) => child !== parentLi,
          );
          siblings.forEach((sibling) => {
            sibling.querySelectorAll("ul").forEach((ul) => slideUp(ul, 200));
            sibling.classList.remove("is-open");
            sibling
              .querySelectorAll("li")
              .forEach((li) => li.classList.remove("is-open"));
          });
        }
      });
    });
  }

  /**
   * Header Search Toggle (Inline on Desktop > 992, in Mobile Menu after 992)
   */
  let lastToggleTime = 0;

  window.ultamationToggleSearch = function (force) {
    const now = Date.now();
    if (typeof force !== "boolean" && now - lastToggleTime < 250) {
      return;
    }
    lastToggleTime = now;

    const desktopWrap = $("#header-search-wrap");
    const navContainer = $(".header-nav-container");
    const navWooIcons = $(".header-nav-container .woo-header-icons");
    const toggles = $$(".header-search-toggle");
    const isDesktop = window.innerWidth > 992;

    if (!isDesktop) {
      // Mobile (after 992): Search is inside the mobile menu
      const mobileNav = $(".mobile-navigation");
      const mobileSearchInput = $(".mobile-menu-search .mobile-search-input");
      const hamburger = $(".hamburger");

      if (mobileNav && !hasClass(mobileNav, "is-open")) {
        mobileNav.classList.add("is-open");
        document.body.classList.add("menu-open");
        if (hamburger) attr(hamburger, "aria-expanded", true);
        hamburgerOpen = true;
        disableScroll();
      }
      if (mobileSearchInput) {
        setTimeout(() => mobileSearchInput.focus(), 150);
      }
      return;
    }

    if (!desktopWrap) return;

    const isCurrentlyActive =
      hasClass(desktopWrap, "is-active") || hasClass(desktopWrap, "active");
    const willBeActive =
      typeof force === "boolean" ? force : !isCurrentlyActive;

    if (willBeActive) {
      desktopWrap.classList.add("is-active", "active");
      if (navContainer) navContainer.classList.add("active", "is-active");
      if (navWooIcons) navWooIcons.classList.add("active", "is-active");
      toggles.forEach((btn) => {
        attr(btn, "aria-expanded", true);
        btn.classList.add("is-active", "active");
      });
      const input = desktopWrap.querySelector(".header-search-input");
      if (input) {
        setTimeout(() => input.focus({ preventScroll: true }), 150);
      }
    } else {
      desktopWrap.classList.remove("is-active", "active");
      if (navContainer) navContainer.classList.remove("active", "is-active");
      if (navWooIcons) navWooIcons.classList.remove("active", "is-active");
      toggles.forEach((btn) => {
        attr(btn, "aria-expanded", false);
        btn.classList.remove("is-active", "active");
      });
    }
  };

  // Prevent empty submissions on mobile search form
  const mobileSearchForm = $(".mobile-menu-search .mobile-search-form");
  if (mobileSearchForm) {
    mobileSearchForm.addEventListener("submit", (e) => {
      const input = mobileSearchForm.querySelector(".mobile-search-input");
      if (input && !input.value.trim()) {
        e.preventDefault();
        input.focus();
      }
    });
  }

  if (!window.__headerSearchEventsBound) {
    window.__headerSearchEventsBound = true;

    document.addEventListener("click", (e) => {
      const desktopWrap = $("#header-search-wrap");
      const navContainer = $(".header-nav-container");
      const navWooIcons = $(".header-nav-container .woo-header-icons");
      const toggle =
        e.target.closest && e.target.closest(".header-search-toggle");

      if (toggle) {
        // If clicked toggle inside desktop search form
        if (desktopWrap && desktopWrap.contains(toggle)) {
          const input = desktopWrap.querySelector(".header-search-input");
          const isActive =
            hasClass(desktopWrap, "is-active") ||
            hasClass(desktopWrap, "active");

          if (!isActive) {
            e.preventDefault();
            e.stopPropagation();
            if (navContainer) navContainer.classList.add("active", "is-active");
            if (navWooIcons) navWooIcons.classList.add("active", "is-active");
            window.ultamationToggleSearch(true);
            return;
          } else {
            // Already active
            if (input && input.value.trim().length > 0) {
              // Submit form (default action)
              return;
            } else {
              // Empty search -> close
              e.preventDefault();
              e.stopPropagation();
              if (navContainer) navContainer.classList.remove("active", "is-active");
              if (navWooIcons) navWooIcons.classList.remove("active", "is-active");
              window.ultamationToggleSearch(false);
              return;
            }
          }
        }

        // Mobile or other toggle
        e.preventDefault();
        window.ultamationToggleSearch();
        return;
      }

      // Close if clicked outside desktop search
      if (
        desktopWrap &&
        (hasClass(desktopWrap, "is-active") || hasClass(desktopWrap, "active"))
      ) {
        if (!desktopWrap.contains(e.target)) {
          if (navContainer) navContainer.classList.remove("active", "is-active");
          if (navWooIcons) navWooIcons.classList.remove("active", "is-active");
          window.ultamationToggleSearch(false);
        }
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        const desktopWrap = $("#header-search-wrap");
        const navContainer = $(".header-nav-container");
        const navWooIcons = $(".header-nav-container .woo-header-icons");
        if (
          desktopWrap &&
          (hasClass(desktopWrap, "is-active") || hasClass(desktopWrap, "active"))
        ) {
          if (navContainer) navContainer.classList.remove("active", "is-active");
          if (navWooIcons) navWooIcons.classList.remove("active", "is-active");
          window.ultamationToggleSearch(false);
        }
      }
    });
  }

  /**
   * Disable scroll when menu is open
   */
  function disableScroll() {
    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    window.onscroll = function () {
      window.scrollTo(scrollLeft, scrollTop);
    };
  }

  /**
   * Enable scroll when menu is closed
   */
  function enableScroll() {
    window.onscroll = function () { };
  }
});
